import React, { useMemo, useState } from 'react';
import axios from 'axios';
import {
  collection,
  deleteDoc,
  doc,
  getDocs,
  limit,
  query,
  setDoc,
  writeBatch,
} from 'firebase/firestore';
import { ref as storageRef, getDownloadURL } from 'firebase/storage';
import {
  Activity,
  AlertCircle,
  Building2,
  ChevronDown,
  ChevronUp,
  Database,
  DownloadCloud,
  FileText,
  Layers,
  // Aliased: lucide's `Map` icon would otherwise shadow the global `Map`
  // constructor and crash the lifecycleOptions useMemo with
  // "b.A is not a constructor" when bundled.
  Map as MapIcon,
  Search,
  Shield,
  Trash2,
  Upload,
} from 'lucide-react';
import { auth, db, storage } from '../../firebase';
import { EcointelLottie, EcointelLottieMark } from '../../components/EcoIntelLoader';
import { UploadForm } from '../../components/UploadForm';
import { useUserFacingError } from '../../hooks/useUserFacingError';
import { resolveLifecycleState } from '../../constants/buildStatus';
import ConfigUploadModal from '../../components/admin/ConfigUploadModal';
import LifecycleModal from '../../components/admin/LifecycleModal';
import ParcelDeleteConfirmModal from '../../components/admin/ParcelDeleteConfirmModal';
import { AdminToolbar, AdminPagination } from '../../components/admin/AdminListToolbar';
import useAdminListState from '../../components/admin/useAdminListState';
import { useAdminData } from '../../components/admin/AdminLayout';

const IS_LOCAL = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
const CLOUD_URL = 'https://analytics-server-652526389082.europe-west2.run.app';
const LOCAL_URL = 'http://localhost:8080';
const SERVER_URL = IS_LOCAL ? LOCAL_URL : CLOUD_URL;

/** Delete all documents under farms/{farmId}/cache (batched; admin can write per firestore.rules). */
async function deleteAllDocsInFarmCache(farmId) {
  const cacheRef = collection(db, 'farms', farmId, 'cache');
  let total = 0;
  while (true) {
    const snap = await getDocs(query(cacheRef, limit(500)));
    if (snap.empty) break;
    const batch = writeBatch(db);
    snap.docs.forEach((d) => batch.delete(d.ref));
    await batch.commit();
    total += snap.docs.length;
    if (snap.size < 500) break;
  }
  return total;
}

const STATUS_FILTER_OPTIONS = [
  { value: 'READY', label: 'Ready' },
  { value: 'UPLOADED', label: 'Uploaded' },
  { value: 'PROCESSING', label: 'Processing' },
  { value: 'COMPLETED', label: 'Completed' },
  { value: 'ERROR', label: 'Error' },
];

const SOURCE_FILTER_OPTIONS = [
  { value: 'GEE', label: 'Earth Engine' },
  { value: 'KMZ', label: 'KMZ upload' },
];

const HAS_PARCELS_FILTER_OPTIONS = [
  { value: 'has', label: 'Has parcels' },
  { value: 'none', label: 'No parcels' },
];

const SHOWCASE_FILTER_OPTIONS = [
  { value: 'showcase', label: 'Showcase only' },
  { value: 'public_demo', label: 'Public demo only' },
];

const SORT_OPTIONS = [
  { value: 'name_asc', label: 'Name (A→Z)', getValue: (f) => (f.farmName || f.name || f.id || '').toLowerCase(), direction: 'asc' },
  { value: 'name_desc', label: 'Name (Z→A)', getValue: (f) => (f.farmName || f.name || f.id || '').toLowerCase(), direction: 'desc' },
  { value: 'parcels_desc', label: 'Most parcels', getValue: (f) => Number(f.parcelCount || 0), direction: 'desc' },
  { value: 'parcels_asc', label: 'Fewest parcels', getValue: (f) => Number(f.parcelCount || 0), direction: 'asc' },
  { value: 'updated_desc', label: 'Recently updated', getValue: (f) => (f.lastUpdate ? Date.parse(f.lastUpdate) || 0 : 0), direction: 'desc' },
  { value: 'lifecycle_asc', label: 'Lifecycle', getValue: (f) => resolveLifecycleState(f).label, direction: 'asc' },
];

export default function PropertiesAdmin() {
  const { farms, refresh } = useAdminData();
  const { formatError, logError, logWarn } = useUserFacingError();

  const [statusFilter, setStatusFilter] = useState([]);
  const [sourceFilter, setSourceFilter] = useState([]);
  const [lifecycleFilter, setLifecycleFilter] = useState([]);
  const [parcelsFilter, setParcelsFilter] = useState([]);
  const [showcaseFilter, setShowcaseFilter] = useState([]);
  const [sortValue, setSortValue] = useState('name_asc');

  // Asset State
  const [eeAssets, setEeAssets] = useState([]);
  const [loadingAssets, setLoadingAssets] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [statusMsg, setStatusMsg] = useState('');
  const [eeSearch, setEeSearch] = useState('');

  const [deletingFarm, setDeletingFarm] = useState(null);
  const [clearingFarmCacheId, setClearingFarmCacheId] = useState(null);
  const [lifecycleModalFarm, setLifecycleModalFarm] = useState(null);
  const [expandedFarmIds, setExpandedFarmIds] = useState(() => new Set());
  const [parcelsByFarm, setParcelsByFarm] = useState({});
  const [deleteParcelTarget, setDeleteParcelTarget] = useState(null);
  const [deletingParcelKey, setDeletingParcelKey] = useState(null);
  const [configModal, setConfigModal] = useState(null);
  const [showUploadKmz, setShowUploadKmz] = useState(false);

  const lifecycleOptions = useMemo(() => {
    const seen = new Map();
    (farms || []).forEach((f) => {
      const lc = resolveLifecycleState(f);
      if (!seen.has(lc.key)) seen.set(lc.key, lc.label);
    });
    return Array.from(seen.entries()).map(([key, label]) => ({ value: key, label }));
  }, [farms]);

  const filteredFarms = useMemo(() => {
    return (farms || []).filter((f) => {
      if (statusFilter.length) {
        const s = (f.status || '').toUpperCase();
        if (!statusFilter.includes(s)) return false;
      }
      if (sourceFilter.length) {
        const src = f.geeAssetId ? 'GEE' : 'KMZ';
        if (!sourceFilter.includes(src)) return false;
      }
      if (lifecycleFilter.length) {
        const lc = resolveLifecycleState(f);
        if (!lifecycleFilter.includes(lc.key)) return false;
      }
      if (parcelsFilter.length) {
        const has = Number(f.parcelCount || 0) > 0;
        if (!parcelsFilter.includes(has ? 'has' : 'none')) return false;
      }
      if (showcaseFilter.length) {
        if (showcaseFilter.includes('showcase') && !f.isShowcase) return false;
        if (showcaseFilter.includes('public_demo') && !f.isPublicDemo) return false;
      }
      return true;
    });
  }, [farms, statusFilter, sourceFilter, lifecycleFilter, parcelsFilter, showcaseFilter]);

  const sortConfig = useMemo(() => {
    const opt = SORT_OPTIONS.find((o) => o.value === sortValue) || SORT_OPTIONS[0];
    return { getValue: opt.getValue, direction: opt.direction };
  }, [sortValue]);

  const list = useAdminListState({
    rows: filteredFarms,
    searchPredicate: (f, q) => (
      (f.id || '').toLowerCase().includes(q) ||
      (f.farmName || f.name || '').toLowerCase().includes(q) ||
      (f.address || '').toLowerCase().includes(q) ||
      (f.landOwnerName || '').toLowerCase().includes(q)
    ),
    defaultSort: sortConfig,
    defaultPageSize: 25,
  });

  // Keep the sort in sync when the user picks a new dropdown option.
  React.useEffect(() => {
    list.setSort(sortConfig);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortConfig.getValue, sortConfig.direction]);

  const filterGroups = [
    {
      id: 'status',
      label: 'Status',
      selected: statusFilter,
      onChange: setStatusFilter,
      options: STATUS_FILTER_OPTIONS,
    },
    {
      id: 'source',
      label: 'Source',
      selected: sourceFilter,
      onChange: setSourceFilter,
      options: SOURCE_FILTER_OPTIONS,
    },
    ...(lifecycleOptions.length > 0 ? [{
      id: 'lifecycle',
      label: 'Lifecycle',
      selected: lifecycleFilter,
      onChange: setLifecycleFilter,
      options: lifecycleOptions,
    }] : []),
    {
      id: 'parcels',
      label: 'Parcels',
      selected: parcelsFilter,
      onChange: setParcelsFilter,
      options: HAS_PARCELS_FILTER_OPTIONS,
    },
    {
      id: 'showcase',
      label: 'Visibility',
      selected: showcaseFilter,
      onChange: setShowcaseFilter,
      options: SHOWCASE_FILTER_OPTIONS,
    },
  ];

  const filteredEeAssets = useMemo(() => {
    if (!eeSearch.trim()) return eeAssets;
    const q = eeSearch.toLowerCase();
    return eeAssets.filter((a) => {
      const name = a.id.split('/').pop();
      return name.toLowerCase().includes(q) || (a.type || '').toLowerCase().includes(q);
    });
  }, [eeAssets, eeSearch]);

  const handleImportFarm = (assetId) => {
    setConfigModal({ mode: 'import', assetId });
  };

  const handleUploadConfig = (farmId) => {
    setConfigModal({ mode: 'sync', farmId });
  };

  const handleClearFarmPropertyCache = async (farmId, farmLabel) => {
    const label = farmLabel || farmId;
    if (!window.confirm(
      `Clear cached data for "${label}"?\n\n` +
      `• Deletes all documents in Firestore at farms/${farmId}/cache (e.g. carbon intelligence).\n` +
      `• Clears farm-scoped analytics server caches (summaries, curves) for this property.`
    )) return;

    setClearingFarmCacheId(farmId);
    try {
      const firestoreDeleted = await deleteAllDocsInFarmCache(farmId);
      const token = await auth.currentUser.getIdToken(true);
      const res = await axios.post(`${SERVER_URL}/clear-cache`, { farmId }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const srv = res.data?.cleared;
      window.alert(
        `Property cache cleared.\n\n` +
        `Firestore farms/${farmId}/cache: ${firestoreDeleted} document(s) removed.\n` +
        `Analytics server: ${srv?.inMemory ?? '—'} in-memory key(s), ${srv?.firestore ?? '—'} Firestore doc(s) (farm-scoped).`
      );
    } catch (e) {
      logError('Clear farm cache failed', e);
      window.alert(formatError(e) || e.message || 'Failed to clear cache');
    } finally {
      setClearingFarmCacheId(null);
    }
  };

  const handleConfigComplete = (targetFarmId) => {
    setConfigModal(null);
    refresh();
    setParcelsByFarm((prev) => {
      const next = { ...prev };
      delete next[targetFarmId];
      return next;
    });
  };

  const loadParcelsForFarm = async (farmId) => {
    setParcelsByFarm((prev) => ({
      ...prev,
      [farmId]: { loading: true, items: prev[farmId]?.items || [], error: null },
    }));
    try {
      const snap = await getDocs(collection(db, 'farms', farmId, 'parcels'));
      const items = snap.docs.map((d) => ({ docId: d.id, ...d.data() }));
      items.sort((a, b) => {
        const na = (a.displayName || a.name || a.parcel_id || a.docId || '').toString();
        const nb = (b.displayName || b.name || b.parcel_id || b.docId || '').toString();
        return na.localeCompare(nb);
      });
      setParcelsByFarm((prev) => ({ ...prev, [farmId]: { loading: false, items, error: null } }));
    } catch (e) {
      logError('Failed to load parcels', e);
      setParcelsByFarm((prev) => ({
        ...prev,
        [farmId]: { loading: false, items: [], error: formatError(e) },
      }));
    }
  };

  const toggleFarmParcels = (farmId) => {
    setExpandedFarmIds((prev) => {
      const next = new Set(prev);
      if (next.has(farmId)) {
        next.delete(farmId);
      } else {
        next.add(farmId);
        loadParcelsForFarm(farmId);
      }
      return next;
    });
  };

  const handleConfirmDeleteParcel = async () => {
    if (!deleteParcelTarget) return;
    const { farmId, parcel } = deleteParcelTarget;
    const key = `${farmId}|${parcel.docId}`;
    setDeletingParcelKey(key);
    try {
      await deleteDoc(doc(db, 'farms', farmId, 'parcels', parcel.docId));
      const snap = await getDocs(collection(db, 'farms', farmId, 'parcels'));
      await setDoc(doc(db, 'farms', farmId), { parcelCount: snap.size, lastUpdate: new Date().toISOString() }, { merge: true });
      try {
        const cacheSnap = await getDocs(collection(db, 'farms', farmId, 'cache'));
        if (cacheSnap.size > 0) {
          const batch = writeBatch(db);
          cacheSnap.docs.forEach((d) => batch.delete(d.ref));
          await batch.commit();
        }
      } catch (e) {
        logWarn('Firestore cache subcollection clear failed', e);
      }
      try {
        const token = await auth.currentUser.getIdToken(true);
        await axios.post(`${SERVER_URL}/clear-cache`, { farmId }, { headers: { Authorization: `Bearer ${token}` } });
      } catch (e) {
        logWarn('Server cache clear failed', e);
      }
      setDeleteParcelTarget(null);
      await loadParcelsForFarm(farmId);
      refresh();
    } catch (e) {
      logError('Parcel delete failed', e);
      alert(`Failed to delete parcel: ${formatError(e)}`);
    } finally {
      setDeletingParcelKey(null);
    }
  };

  const handleDeleteFarm = async (farmId) => {
    if (!window.confirm(`Remove "${farmId}" and clear all cached data?\n\nThis deletes the property record, all cached analytics, and server-side caches. GEE assets are not affected. You can re-import the property at any time.`)) return;

    setDeletingFarm(farmId);
    try {
      try {
        const token = await auth.currentUser.getIdToken(true);
        await axios.post(`${SERVER_URL}/clear-cache`, { farmId }, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } catch (e) {
        logWarn('Server cache clear failed (continuing)', e);
      }

      const subcollections = ['parcels', 'farmCurves', 'parcelCurves', 'cache', 'meta', 'config', 'managementEvents', 'uploadedReports', 'connectedServices', 'connectedServiceEvents'];
      let totalDeleted = 0;
      for (const sub of subcollections) {
        try {
          const snap = await getDocs(collection(db, 'farms', farmId, sub));
          if (snap.size > 0) {
            // v16: parcelCurves now shards per-parcel under each year doc
            // (parcelCurves/{year}/parcels/{parcelDocId}). Recurse one level
            // deeper so the per-parcel sub-docs are cleaned up before we delete
            // the year doc itself — otherwise orphan curve docs leak on
            // property removal. See PHASE_NEXT_to_richard_integration_response_v14 §5.
            if (sub === 'parcelCurves') {
              for (const yearDoc of snap.docs) {
                try {
                  const inner = await getDocs(collection(db, 'farms', farmId, sub, yearDoc.id, 'parcels'));
                  if (inner.size > 0) {
                    const innerBatch = writeBatch(db);
                    inner.docs.forEach((d) => innerBatch.delete(d.ref));
                    await innerBatch.commit();
                    totalDeleted += inner.size;
                  }
                } catch (innerErr) {
                  logWarn(`Failed to clear farms/${farmId}/${sub}/${yearDoc.id}/parcels`, innerErr);
                }
              }
            }
            const batch = writeBatch(db);
            snap.docs.forEach((d) => batch.delete(d.ref));
            await batch.commit();
            totalDeleted += snap.size;
          }
        } catch (e) {
          logWarn(`Failed to clear farms/${farmId}/${sub}`, e);
        }
      }

      await deleteDoc(doc(db, 'farms', farmId));
      refresh();
    } catch (e) {
      logError('Farm deletion failed', e);
      alert(`Failed to remove ${farmId}: ${formatError(e)}`);
    }
    setDeletingFarm(null);
  };

  const fetchEEAssets = async () => {
    setLoadingAssets(true);
    setHasSearched(true);
    setStatusMsg('Connecting to Earth Engine...');
    setEeAssets([]);

    try {
      const token = await auth.currentUser.getIdToken();
      const folder = 'projects/eddp-ecodynamic-diagnostics/assets';

      const res = await axios.post(`${SERVER_URL}/list-ee-assets`,
        { folder },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const rawList = res.data.assets || [];
      setStatusMsg(`Found ${rawList.length} raw items. Filtering...`);

      const cleanList = rawList.filter((a) => {
        const id = a.id.toLowerCase();
        return !id.includes('archive') && !id.includes('backup');
      });

      setEeAssets(cleanList);
      setStatusMsg(cleanList.length === 0 ? 'No properties found in that folder.' : '');
    } catch (e) {
      const friendly = formatError(e);
      setStatusMsg(`Error: ${friendly}`);
      alert(`Failed to load: ${friendly}`);
    }
    setLoadingAssets(false);
  };

  return (
    <>
      {lifecycleModalFarm ? (
        <LifecycleModal
          farm={lifecycleModalFarm}
          onClose={() => setLifecycleModalFarm(null)}
          onApplied={() => { refresh(); }}
          formatError={formatError}
        />
      ) : null}

      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-3 text-slate-900">
            <Building2 size={22} /> Properties
          </h1>
          <p className="text-xs text-slate-500 mt-0.5">
            {(farms || []).length} total · {filteredFarms.length} match current filters
          </p>
        </div>
      </div>

      <div className="space-y-8">
        {/* App Properties */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
          <AdminToolbar
            search={list.search}
            onSearchChange={list.setSearch}
            searchPlaceholder="Search by name, address, owner…"
            filterGroups={filterGroups}
            toolbarLeft={(
              <div className="hidden lg:flex items-center gap-2 mr-2">
                <Database size={16} className="text-slate-500" />
                <span className="font-bold text-sm text-slate-700">App Properties</span>
              </div>
            )}
            toolbarRight={(
              <label className="flex items-center gap-1.5 text-xs">
                <span className="text-[10px] uppercase tracking-wider text-slate-400">Sort</span>
                <select
                  value={sortValue}
                  onChange={(e) => setSortValue(e.target.value)}
                  className="border border-slate-200 rounded px-2 py-1 text-xs bg-white"
                >
                  {SORT_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </label>
            )}
          />

          <div className="max-h-[36rem] overflow-y-auto">
            {list.visible.length === 0 ? (
              <div className="p-8 text-center text-slate-400">
                {(farms || []).length === 0
                  ? <p>No properties in database.</p>
                  : <p>No properties match the current search and filters.</p>}
              </div>
            ) : (
              list.visible.map((f) => {
                const parcelState = parcelsByFarm[f.id];
                const parcelsOpen = expandedFarmIds.has(f.id);
                return (
                  <div key={f.id} className={`border-b border-slate-50 ${deletingFarm === f.id ? 'opacity-50' : ''}`}>
                    <div className="p-3 hover:bg-slate-50">
                      <div className="flex justify-between items-start">
                        <div className="flex-1 min-w-0">
                          <div className="font-bold text-sm">{f.farmName || f.name || f.id}</div>
                          {f.address && <div className="text-[11px] text-slate-500 truncate">{f.address}</div>}
                          {f.landOwnerName && <div className="text-[10px] text-slate-400">Owner: {f.landOwnerName}</div>}
                          <div className="flex items-center gap-2 mt-1 flex-wrap">
                            <span className="text-[10px] text-slate-400">
                              {deletingFarm === f.id ? 'Removing & clearing caches...' : (f.geeAssetId ? 'Linked to GEE' : 'Uploaded File')}
                            </span>
                            {(() => {
                              const lc = resolveLifecycleState(f);
                              return (
                                <button
                                  type="button"
                                  onClick={() => setLifecycleModalFarm(f)}
                                  className={`text-[10px] px-1.5 py-0.5 rounded font-medium border border-current/20 ${lc.pillClass || 'bg-slate-100'} ${lc.textClass || 'text-slate-700'} hover:opacity-80 transition-opacity inline-flex items-center gap-1`}
                                  title={`Build state: ${f.buildStatus || lc.key} \u2014 click to manage`}
                                >
                                  <Activity size={10} />
                                  {lc.label}
                                </button>
                              );
                            })()}
                            {f.isShowcase && <span className="text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded font-medium">Demo</span>}
                            {f.isPublicDemo && <span className="text-[10px] bg-sky-100 text-sky-700 px-1.5 py-0.5 rounded font-medium">Public demo</span>}
                            {f.configImportedAt && <span className="text-[10px] bg-emerald-50 text-emerald-600 px-1.5 py-0.5 rounded font-medium">Config</span>}
                            {f.intakeKmz?.storage_path && (
                              <span className="text-[10px] bg-emerald-50 text-emerald-700 px-1.5 py-0.5 rounded font-medium inline-flex items-center gap-1" title={`Customer-supplied boundary file: ${f.intakeKmz.original_filename || f.intakeKmz.storage_path}`}>
                                <FileText size={10} /> Intake KMZ
                              </span>
                            )}
                            {f.parcelCount != null && <span className="text-[10px] text-slate-400">{f.parcelCount} parcels</span>}
                          </div>
                        </div>
                        <div className="flex items-center gap-1 shrink-0 ml-2">
                          {/* Customer-supplied boundary file uploaded via
                              the PropertyIntake form. Surfaced here so an
                              admin can pull the original KMZ down (e.g.
                              to forward to Marcus's RB pipeline manually
                              or to verify what the customer sent). The
                              file lives in GCS under
                              intake-uploads/{uid}/... and the catch-all
                              storage rule grants admins read access. */}
                          {f.intakeKmz?.storage_path && (
                            <button
                              onClick={async () => {
                                try {
                                  const url = await getDownloadURL(storageRef(storage, f.intakeKmz.storage_path));
                                  window.open(url, '_blank', 'noopener,noreferrer');
                                } catch (err) {
                                  logError('Intake KMZ download failed', err);
                                  alert(`Could not open the intake KMZ: ${formatError(err)}`);
                                }
                              }}
                              className="text-xs px-2 py-1 rounded border border-emerald-200 text-emerald-700 hover:bg-emerald-50 flex items-center gap-1 transition-colors"
                              title={`Download customer-supplied boundary file (${f.intakeKmz.original_filename || 'intake KMZ'})`}
                            >
                              <DownloadCloud size={12} /> Intake KMZ
                            </button>
                          )}
                          <button
                            onClick={() => handleUploadConfig(f.id)}
                            className="text-xs px-2 py-1 rounded border border-slate-200 text-slate-500 hover:text-[#043132] hover:border-[#043132] flex items-center gap-1 transition-colors"
                            title="Upload config script to sync parcel metadata"
                          >
                            <Upload size={12} /> Config
                          </button>
                          <button
                            type="button"
                            onClick={() => handleClearFarmPropertyCache(f.id, f.farmName || f.name)}
                            disabled={clearingFarmCacheId != null}
                            className="text-xs px-2 py-1 rounded border border-slate-200 text-slate-500 hover:text-amber-700 hover:border-amber-300 flex items-center gap-1 transition-colors disabled:opacity-40"
                            title={`Clear farms/${f.id}/cache and server-side caches for this property`}
                          >
                            {clearingFarmCacheId === f.id ? <EcointelLottieMark size={12} /> : <Database size={12} />}
                            Cache
                          </button>
                          <button
                            onClick={async () => {
                              await setDoc(doc(db, 'farms', f.id), { isShowcase: !f.isShowcase }, { merge: true });
                              refresh();
                            }}
                            className={`text-xs px-2 py-1 rounded border ${f.isShowcase ? 'bg-amber-50 border-amber-200 text-amber-700' : 'border-slate-200 text-slate-400 hover:text-slate-600'}`}
                            title={f.isShowcase ? 'Remove from demo properties' : 'Mark as demo property'}
                          >
                            {f.isShowcase ? 'Demo ✓' : 'Demo'}
                          </button>
                          <button onClick={() => handleDeleteFarm(f.id)} disabled={!!deletingFarm} className="text-red-400 hover:text-red-600 disabled:opacity-30 disabled:cursor-not-allowed p-1">
                            {deletingFarm === f.id ? <EcointelLottieMark size={16} /> : <Trash2 size={16} />}
                          </button>
                        </div>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => toggleFarmParcels(f.id)}
                      className="w-full px-3 py-2.5 flex items-center justify-between text-left text-xs font-medium text-slate-600 bg-slate-50/90 hover:bg-slate-100 border-t border-slate-100 transition-colors"
                    >
                      <span className="flex items-center gap-2">
                        <Layers size={14} className="text-slate-400" />
                        Parcel documents
                        {parcelState?.items && (
                          <span className="text-[10px] font-semibold bg-slate-200 text-slate-600 px-1.5 py-0.5 rounded-full">{parcelState.items.length}</span>
                        )}
                      </span>
                      {parcelsOpen ? <ChevronUp size={14} className="text-slate-400" /> : <ChevronDown size={14} className="text-slate-400" />}
                    </button>
                    {parcelsOpen && (
                      <div className="px-3 pb-3 bg-slate-50/80 border-t border-slate-100">
                        {parcelState?.loading && (
                          <div className="flex items-center justify-center py-6 text-slate-500 text-sm gap-2">
                            <EcointelLottieMark size={18} /> Loading parcels…
                          </div>
                        )}
                        {parcelState?.error && (
                          <div className="py-3 text-sm text-red-600 flex items-start gap-2">
                            <AlertCircle size={16} className="mt-0.5 shrink-0" />
                            {parcelState.error}
                          </div>
                        )}
                        {!parcelState?.loading && !parcelState?.error && parcelState?.items && (
                          parcelState.items.length === 0 ? (
                            <p className="py-4 text-center text-sm text-slate-400">No parcel documents for this property.</p>
                          ) : (
                            <div className="border border-slate-200 rounded-lg overflow-hidden bg-white">
                              <table className="w-full text-left text-sm">
                                <thead className="bg-slate-50 text-[10px] text-slate-500 uppercase font-bold border-b border-slate-100">
                                  <tr>
                                    <th className="p-2.5">Name</th>
                                    <th className="p-2.5">Doc ID</th>
                                    <th className="p-2.5">Type</th>
                                    <th className="p-2.5 text-right">Area (ha)</th>
                                    <th className="p-2.5">Source</th>
                                    <th className="p-2.5 text-right">Actions</th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-50">
                                  {parcelState.items.map((p) => {
                                    const isWs =
                                      p.analyticsParcelName === 'WHOLE_SITE' ||
                                      p.name === 'WHOLE_SITE' ||
                                      (p.parcel_id || '').toString().trim() === 'WHOLE_SITE';
                                    const rowKey = `${f.id}|${p.docId}`;
                                    const sortedJson = JSON.stringify(
                                      { docId: p.docId, ...Object.fromEntries(
                                        Object.entries(p).filter(([k]) => k !== 'docId').sort(([a], [b]) => a.localeCompare(b))
                                      ) },
                                      null,
                                      2
                                    );
                                    return (
                                      <React.Fragment key={p.docId}>
                                        <tr className="hover:bg-slate-50/80 align-top">
                                          <td className="p-2.5">
                                            <div className="font-medium text-slate-800">{p.displayName || p.name || '—'}</div>
                                            {p.farm_field_name && (
                                              <div className="text-[10px] text-slate-400 mt-0.5">{p.farm_field_name}</div>
                                            )}
                                          </td>
                                          <td className="p-2.5 font-mono text-[10px] text-slate-600 break-all max-w-[140px]">{p.docId}</td>
                                          <td className="p-2.5 text-xs text-slate-600">{p.type || '—'}</td>
                                          <td className="p-2.5 text-right text-xs tabular-nums">
                                            {p.area_ha != null && p.area_ha !== '' ? Number(p.area_ha).toFixed(2) : '—'}
                                          </td>
                                          <td className="p-2.5 text-[10px] text-slate-500">{p.source || '—'}</td>
                                          <td className="p-2.5 text-right">
                                            <button
                                              type="button"
                                              onClick={() => setDeleteParcelTarget({
                                                farmId: f.id,
                                                farmLabel: f.farmName || f.name || f.id,
                                                parcel: p,
                                              })}
                                              disabled={!!deletingParcelKey || isWs}
                                              title={isWs ? 'Whole-site parcel cannot be deleted here' : 'Delete parcel'}
                                              className="p-1.5 text-slate-400 hover:text-red-600 rounded hover:bg-red-50 disabled:opacity-30 disabled:cursor-not-allowed inline-flex"
                                            >
                                              {deletingParcelKey === rowKey ? <EcointelLottieMark size={14} /> : <Trash2 size={14} />}
                                            </button>
                                          </td>
                                        </tr>
                                        <tr className="bg-slate-50/50">
                                          <td colSpan={6} className="px-2.5 pb-2.5 pt-0">
                                            <details className="text-[10px] group">
                                              <summary className="cursor-pointer text-[#043132] font-medium hover:underline select-none">
                                                All stored fields ({Object.keys(p).length})
                                              </summary>
                                              <pre className="mt-2 p-2 rounded border border-slate-200 bg-white text-slate-600 overflow-x-auto max-h-40 overflow-y-auto whitespace-pre-wrap break-all">
                                                {sortedJson}
                                              </pre>
                                            </details>
                                          </td>
                                        </tr>
                                      </React.Fragment>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                          )
                        )}
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>

          <AdminPagination
            page={list.page}
            setPage={list.setPage}
            pageCount={list.pageCount}
            pageSize={list.pageSize}
            setPageSize={list.setPageSize}
            total={list.total}
            rawTotal={(farms || []).length}
            showingFrom={list.showingFrom}
            showingTo={list.showingTo}
          />
        </div>

        {/* Earth Engine Import */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
          <div className="p-4 border-b bg-slate-50">
            <div className="flex items-center justify-between mb-3">
              <span className="font-bold text-sm text-slate-700 flex items-center gap-2"><MapIcon size={16} /> Import from Earth Engine</span>
              <button onClick={fetchEEAssets} disabled={loadingAssets} className="text-xs border border-slate-200 px-3 py-1.5 rounded-lg hover:bg-white bg-slate-50 font-medium disabled:opacity-50 transition-colors">
                {loadingAssets ? 'Loading...' : 'Refresh List'}
              </button>
            </div>
            {eeAssets.length > 0 && (
              <div className="relative">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  value={eeSearch}
                  onChange={(e) => setEeSearch(e.target.value)}
                  placeholder="Search Earth Engine assets..."
                  className="w-full pl-9 pr-16 py-2 border border-slate-200 rounded-lg text-sm focus:border-[#043132] focus:ring-1 focus:ring-[#043132] outline-none bg-white"
                />
                {eeSearch && (
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] font-medium text-slate-400">
                    {filteredEeAssets.length} results
                  </span>
                )}
              </div>
            )}
          </div>
          <div className="max-h-[28rem] overflow-y-auto relative">
            {loadingAssets && (
              <div className="absolute inset-0 bg-white/85 flex flex-col items-center justify-center z-10 px-4">
                <EcointelLottie message={statusMsg || 'Loading Earth Engine assets...'} lottiePixelWidth={96} lottiePixelHeight={120} />
              </div>
            )}

            {!loadingAssets && eeAssets.length === 0 && (
              <div className="flex flex-col items-center justify-center py-12 text-slate-400 px-6 text-center">
                {hasSearched ? (
                  <>
                    <AlertCircle size={32} className="mb-2 opacity-50"/>
                    <p className="font-bold text-slate-600">No Assets Found</p>
                    <p className="text-xs mt-1">Checked: projects/eddp-ecodynamic-diagnostics</p>
                    <p className="text-xs mt-1">{statusMsg}</p>
                  </>
                ) : (
                  <p>Click "Refresh List" to load properties from Earth Engine.</p>
                )}
              </div>
            )}

            {filteredEeAssets.length === 0 && eeAssets.length > 0 && eeSearch && (
              <div className="p-8 text-center text-slate-400">
                <Search size={28} className="mx-auto mb-2 opacity-40" />
                <p className="font-medium text-slate-600">No assets match "{eeSearch}"</p>
                <button onClick={() => setEeSearch('')} className="text-xs text-[#043132] hover:underline mt-2">Clear search</button>
              </div>
            )}

            {filteredEeAssets.map((a) => {
              const name = a.id.split('/').pop();
              const isImported = (farms || []).some((f) => f.id === name);
              return (
                <div key={a.id} className="flex justify-between items-center p-3 border-b border-slate-50 hover:bg-slate-50">
                  <div className="truncate flex-1 mr-3" title={a.id}>
                    <div className="font-bold text-sm">{name}</div>
                    <div className="text-[10px] text-slate-400 uppercase">{a.type}</div>
                  </div>
                  {isImported ? (
                    <span className="text-xs text-green-600 font-bold px-2 flex items-center gap-1 shrink-0">
                      <Shield size={10}/> Imported
                    </span>
                  ) : (
                    <button onClick={() => handleImportFarm(a.id)} className="bg-[#043132] text-white text-xs px-3 py-1.5 rounded-lg flex items-center gap-1 hover:bg-[#065354] shrink-0 transition-colors">
                      <DownloadCloud size={12} /> Import
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Upload KMZ (Admin only, collapsible) */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
          <button
            onClick={() => setShowUploadKmz(!showUploadKmz)}
            className="w-full p-4 flex items-center justify-between text-left hover:bg-slate-50 transition-colors"
          >
            <span className="font-bold text-sm text-slate-700 flex items-center gap-2"><Upload size={16} /> Upload KMZ / KML File</span>
            {showUploadKmz ? <ChevronUp size={16} className="text-slate-400" /> : <ChevronDown size={16} className="text-slate-400" />}
          </button>
          {showUploadKmz && (
            <div className="p-4 border-t border-slate-100">
              <UploadForm
                onClose={() => setShowUploadKmz(false)}
                onUploadComplete={() => {
                  setShowUploadKmz(false);
                  refresh();
                }}
              />
            </div>
          )}
        </div>
      </div>

      {deleteParcelTarget && (
        <ParcelDeleteConfirmModal
          farmId={deleteParcelTarget.farmId}
          farmLabel={deleteParcelTarget.farmLabel}
          parcel={deleteParcelTarget.parcel}
          onClose={() => { if (!deletingParcelKey) setDeleteParcelTarget(null); }}
          onConfirm={handleConfirmDeleteParcel}
          deleting={deletingParcelKey === `${deleteParcelTarget.farmId}|${deleteParcelTarget.parcel.docId}`}
        />
      )}

      {configModal && (
        <ConfigUploadModal
          mode={configModal.mode}
          assetId={configModal.assetId}
          farmId={configModal.farmId}
          onClose={() => setConfigModal(null)}
          onComplete={handleConfigComplete}
          serverUrl={SERVER_URL}
        />
      )}
    </>
  );
}
